import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd, RouterOutlet } from '@angular/router';
import { environment } from '../environments/environment';
import { AuthStateService } from './services/auth-state.service';
import { LogoutLoadingService } from './services/logout-loading.service';
import { BuildVersionService } from './services/build-version.service';
import { DialogService } from './services/dialog.service';
import { StorageService } from './services/storage.service';
import { SidemenuComponent } from './components/sidemenu/sidemenu.component';
import { HeaderComponent } from './components/header/header.component';
import { PatientChatComponent } from './components/patient-chat/patient-chat.component';
import { NewVersionDialogComponent } from './components/new-version-dialog/new-version-dialog.component';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-root',
  imports: [CommonModule, RouterOutlet, SidemenuComponent, HeaderComponent, PatientChatComponent],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  isLoggedIn = false;
  showLayout = false;
  isLoggingOut = false;
  isQuickActivated = false;
  // PE-0000 — portal assistant only on local/dev builds
  readonly showPatientChat = environment.showPatientChat;
  private subscription: Subscription = new Subscription();
  private buildUpdateHandler: (() => void) | null = null;
  private storageService = inject(StorageService);

  // Routes that should not show the layout (header/sidemenu) even when logged in
  private readonly noLayoutRoutes = [
    'page-not-found',
    'forgot-username',
    'forgot-password',
    'patient-mfa',
    'sign-up', // PE-6148 — guest registration hides header/sidemenu
    'associatedPatients', // PE-6224 — patient picker hides header/sidemenu until a patient is selected
  ] as const;

  constructor(
    private authStateService: AuthStateService,
    private router: Router,
    private logoutLoadingService: LogoutLoadingService,
    private buildVersionService: BuildVersionService,
    private dialogService: DialogService
  ) {}

  ngOnInit(): void {
    // Preload sign-in component chunk early if user is not logged in
    // This improves initial page load performance for the default landing page
    // The HomePreloadStrategy also handles this, but we do it here as well for
    // even earlier preloading since sign-in is the default page
    if (!this.authStateService.isLoggedIn) {
      // Preload sign-in component chunk in the background immediately
      // This starts downloading the chunk as soon as the app component initializes
      import('./pages/sign-in/sign-in.component').catch(() => {
        // Silently fail - chunk will load during navigation anyway
      });
    }

    // check for new builds on guest routes (sign-in) as well as authenticated routes
    this.buildVersionService.startChecking();

    // Subscribe to authentication state changes
    this.subscription.add(
      this.authStateService.isLoggedIn$.subscribe(isLoggedIn => {
        this.isLoggedIn = isLoggedIn;
        this.checkQuickActivatedStatus();
        this.updateLayoutVisibility();
      })
    );

    // Listen for build update events
    this.buildUpdateHandler = this.handleBuildUpdate.bind(this);
    window.addEventListener('app-build-update', this.buildUpdateHandler);

    // Subscribe to route changes
    this.subscription.add(
      this.router.events
        .pipe(filter(event => event instanceof NavigationEnd))
        .subscribe((event: NavigationEnd) => {
          this.checkQuickActivatedStatus();
          this.updateLayoutVisibility();
          // Hide logout loader when navigation completes
          this.logoutLoadingService.setLoading(false);
          // PE-00 — version check on every navigation (guest + authenticated)
          this.buildVersionService.checkOnNavigation();
        })
    );

    // Subscribe to logout loading state
    this.subscription.add(
      this.logoutLoadingService.isLoading$.subscribe(isLoading => {
        this.isLoggingOut = isLoading;
      })
    );
  }

  ngOnDestroy(): void {
    // Cleanup all subscriptions to prevent memory leaks
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    
    // Stop build version checking
    this.buildVersionService.stopChecking();
    
    // Remove event listener
    if (this.buildUpdateHandler) {
      window.removeEventListener('app-build-update', this.buildUpdateHandler);
      this.buildUpdateHandler = null;
    }
  }

  /**
   * Handles build update detection - shows dialog to user
   * PE-00 — shown on guest routes as well as authenticated routes
   */
  private handleBuildUpdate(): void {
    if (!this.dialogService.isOpen()) {
      this.dialogService.open(NewVersionDialogComponent);
    }
  }

  private checkQuickActivatedStatus(): void {
    if (this.isLoggedIn) {
      this.isQuickActivated = this.storageService.getSessionStorage('is_quick_activated') === true;
    } else {
      this.isQuickActivated = false;
    }
  }

  private updateLayoutVisibility(): void {
    const currentUrl = this.router.url;
    
    // Check if we're on a no-layout route
    const isNoLayoutRoute = this.noLayoutRoutes.some(route => currentUrl.includes(route));
    
    // Check if we're on the sign-in route (dynamic route with tenant code)
    const isSignInRoute = this.isSignInRoute(currentUrl);
    
    // Hide layout for QuickActivated users (they should see quick-activate component without side menu)
    // Show layout only if user is logged in AND not on a no-layout route AND not on sign-in route AND not QuickActivated
    const shouldShowLayout =
      this.isLoggedIn &&
      !isNoLayoutRoute &&
      !isSignInRoute &&
      !this.isQuickActivated;
    this.showLayout = shouldShowLayout;
  }

  private isSignInRoute(url: string): boolean {
    // The sign-in route pattern is /:tenantShortName (e.g., /glo9050Demo)
    // We can identify it by checking if the URL matches the pattern and doesn't contain other known routes
    const segments = url.split('/').filter(segment => segment.length > 0);
    
    // If there's only one segment and it's not a known route, it's likely a tenant short name (sign-in route)
    if (segments.length === 1) {
      const firstSegment = segments[0];
      const knownRoutes = [
        'home', 'dashboard', 'audit-log', 'messages', 'restriction', 'ask-provider', 'profile',
        'account-settings', 'appointments', 'book-appointment', 'health', 'health-record', 'statements', 'pay-bills', 'account-on-file',
        'patient-forms', 'patientMsg', 'page-not-found', 'forgot-username', 'forgot-password', 'phi', 'patient-mfa',
        'associatedPatients', 'quick-activate', 'sign-up'
      ];
      
      return !knownRoutes.includes(firstSegment);
    }
    
    return false;
  }
}